import React from "react";
import { useState, useEffect } from "react";
import { Typography, Input, Button, Option, Checkbox } from "@material-tailwind/react";
import { ArrowLeftIcon, MinusCircleIcon, PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AddProductColor from "./add-color-modal";
import { useSelector, useDispatch } from "react-redux";
import { addProduct, fetchMainCategories, fetchCategories, fetchSubCategories, fetchVendor, deleteProductImage, resetStatus, fetchBrand, fetchColorAttribute, fetchSizeAttribute, fetchProductAttribute, fetchProductAttributeValue, fetchProductVarient, fetchCity } from "@/store/slices/productSlice";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import Select from 'react-select';
import { Link, useParams } from "react-router-dom";
import Toaster from "../../components/Toaster";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ShowLoader from "@/components/Loader";
import { fetchCountry } from "@/store/slices/internalUsersSlice";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";
import { getCurrencies } from "@/store/slices/currentCountrySlice";

const notBlankRegex = /\S/;
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = (t) => yup.object().shape({
  sku_number: yup
    .string()
    .required(t("errors.sku_number.required"))
    .matches(notBlankRegex, t("errors.sku_number.notBlankRegex"))
    .matches(noHTMLRegex, t("errors.sku_number.noHTMLRegex"))
    .matches(noCodeRegex, t("errors.sku_number.noCodeRegex"))
    .matches(noConsecutiveSpecialChars, t("errors.sku_number.noConsecutiveSpecialChars"))
    .matches(noLeadingTrailingPunct, t("errors.sku_number.noLeadingTrailingPunct"))
    .min(2, t("errors.sku_number.min"))
    .max(15, t("errors.sku_number.max")),

  name: yup
    .string()
    .required(t("errors.name.required"))
    .matches(notBlankRegex, t("errors.name.notBlankRegex"))
    .matches(noHTMLRegex, t("errors.name.noHTMLRegex"))
    .matches(noCodeRegex, t("errors.name.noCodeRegex"))
    .matches(noConsecutiveSpecialChars, t("errors.name.noConsecutiveSpecialChars"))
    .matches(noLeadingTrailingPunct, t("errors.name.noLeadingTrailingPunct"))
    .min(2, t("errors.name.min"))
    .max(30, t("errors.name.max")),

  category_id: yup
    .number()
    .typeError(t("errors.category_id.typeError"))
    .required(t("errors.category_id.required")),

  main_category_id: yup
    .number()
    .typeError(t("errors.main_category_id.typeError"))
    .required(t("errors.main_category_id.required")),

  sub_category_id: yup
    .number()
    .typeError(t("errors.sub_category_id.typeError"))
    .required(t("errors.sub_category_id.required")),

  short_description: yup
    .string()
    .required(t("errors.short_description.required"))
    .matches(notBlankRegex, t("errors.short_description.notBlankRegex"))
    .matches(noCodeRegex, t("errors.short_description.noCodeRegex"))
    .max(500, t("errors.short_description.max")),

  // installation_charge: yup
  //     .number()
  //     .typeError(t("errors.installation_charge.typeError"))
  //     .min(0, t("errors.installation_charge.min")),
  installation_charge: yup
    .object()
    .test(
      "valid-installation-charges",
      t("errors.installation_charge.typeError"),
      (value) => {
        if (!value) return false;

        return Object.values(value).every((amount) => {
          if (amount === "" || amount === null || amount === undefined) {
            return true;
          }

          return !isNaN(Number(amount)) && Number(amount) >= 0;
        });
      }
    ),

  vat_tax: yup
    .number()
    .typeError(t("errors.vat_tax.typeError"))
    .min(0, t("errors.vat_tax.min")),

  is_featured: yup
    .string()
    .oneOf(["yes", "no"], t("errors.is_featured.oneOf"))
    .required(t("errors.is_featured.required")),

  description: yup
    .string()
    .required(t("errors.description.required"))
    .matches(notBlankRegex, t("errors.description.notBlankRegex"))
    .matches(noCodeRegex, t("errors.description.noCodeRegex"))
    .max(2000, t("errors.description.max")),

  brand_id: yup
    .number()
    .typeError(t("errors.brand_id.typeError"))
    .required(t("errors.brand_id.required")),

  // product_variants: yup.array().of(
  //     yup.object().shape({
  //         attribute_value_color_id: yup
  //             .number()
  //             .typeError(t("errors.product_variants.attribute_value_color_id.typeError"))
  //             .required(t("errors.product_variants.attribute_value_color_id.required")),
  //         attribute_value_size_id: yup
  //             .number()
  //             .typeError(t("errors.product_variants.attribute_value_size_id.typeError"))
  //             .required(t("errors.product_variants.attribute_value_size_id.required")),
  //         base_price: yup
  //             .number()
  //             .required(t("errors.product_variants.base_price.required"))
  //             .typeError(t("errors.product_variants.base_price.typeError"))
  //             .positive(t("errors.product_variants.base_price.positive")),
  //         sale_price: yup
  //             .number()
  //             .required(t("errors.product_variants.sale_price.required"))
  //             .typeError(t("errors.product_variants.sale_price.typeError"))
  //             .when("base_price", (base_price, schema) =>
  //                 base_price
  //                     ? schema.min(
  //                         base_price,
  //                         t("errors.product_variants.sale_price.when")
  //                     )
  //                     : schema
  //             ),
  //     })
  // ),
  product_variants: yup.array().of(
    yup.object().shape({
      attribute_value_color_id: yup
        .number()
        .typeError(
          t(
            "errors.product_variants.attribute_value_color_id.typeError"
          )
        )
        .required(
          t(
            "errors.product_variants.attribute_value_color_id.required"
          )
        ),

      attribute_value_size_id: yup
        .number()
        .typeError(
          t(
            "errors.product_variants.attribute_value_size_id.typeError"
          )
        )
        .required(
          t(
            "errors.product_variants.attribute_value_size_id.required"
          )
        ),

      //   prices: yup
      //     .array()
      //     .of(
      //       yup.object().shape({
      //         currency_id: yup
      //           .number()
      //           .required(),

      //         base_price: yup
      //           .number()
      //           .typeError(
      //             t("errors.product_variants.base_price.typeError")
      //           )
      //           .required(
      //             t("errors.product_variants.base_price.required")
      //           )
      //           .min(
      //             0,
      //             t("errors.product_variants.base_price.min")
      //           ),

      //         sale_price: yup
      //           .number()
      //           .typeError(
      //             t("errors.product_variants.sale_price.typeError")
      //           )
      //           .required(
      //             t("errors.product_variants.sale_price.required")
      //           )
      //           .test(
      //             "sale-price-greater-than-purchase",
      //             t("errors.product_variants.sale_price.when"),
      //             function (value) {
      //               const { base_price } = this.parent;

      //               if (
      //                 value === undefined ||
      //                 base_price === undefined
      //               ) {
      //                 return true;
      //               }

      //               return value >= base_price;
      //             }
      //           ),
      //       })
      //     )
      //     .min(1)
      //     .required(),
    })
  ),

  product_attributes: yup.array()
    .of(
      yup.object().shape({
        attribute_id: yup.string().required(t("errors.product_attributes.attribute_id.required")),
        attribute_value_id: yup.string().required(t("errors.product_attributes.attribute_value_id.required")),
      })
    )
    .test(
      "no-duplicates",
      t("errors.product_attributes.no-duplicates"),
      (items) => {
        if (!items) return true;
        const seen = new Set();
        for (const item of items) {
          const key = `${item.attribute_id}-${item.attribute_value_id}`;
          if (seen.has(key)) return false;
          seen.add(key);
        }
        return true;
      }
    ),

  color_attributes: yup
    .array()
    .of(
      yup.object().shape({
        color_attribute_id: yup.number(),
        image: yup
          .mixed()
          .test("fileOrString", t("errors.color_attributes.image.fileOrString"), (value) => {
            if (!value) return true;
            if (value instanceof File) return value.type.startsWith("image/");
            if (typeof value === "string") return true;
            return false;
          })
          .nullable(),
      })
    )
    .test(
      "at-least-one-image",
      t("errors.color_attributes.at-least-one-image"),
      (colorAttributes) => {
        if (!colorAttributes || colorAttributes.length === 0) return false;
        return colorAttributes.some(
          (item) =>
            item.image &&
            ((item.image instanceof File && item.image.type.startsWith("image/")) ||
              typeof item.image === "string")
        );
      }
    ),
  available_vendors: yup.array().of(
    yup.object().shape({
      product_varient_id: yup
        .number()
        .transform((value, originalValue) => (originalValue === "" ? undefined : value))
        .typeError(t("errors.available_vendors.product_varient_id.typeError")) // Custom message for NaN/Invalid
        .required(t("errors.available_vendors.product_varient_id.required")),

      vendor_ids: yup // Note: Ensure this matches your 'isMulti' Select
        .array()
        .min(1, t("errors.available_vendors.vendor_ids.min"))
        .required(t("errors.available_vendors.vendor_ids.required")),

      country_id: yup
        .number()
        .transform((value, originalValue) => (originalValue === "" ? undefined : value))
        .typeError(t("errors.available_vendors.country_id.typeError"))
        .required(t("errors.available_vendors.country_id.required")),
      city_id: yup
        .number()
        .transform((value, originalValue) => (originalValue === "" ? undefined : value))
        .typeError(t("errors.available_vendors.city_id.typeError"))
        .required(t("errors.available_vendors.city_id.required")),

      price: yup
        .number()
        .transform((value, originalValue) =>
          originalValue === "" ? undefined : value
        )
        .typeError(t("errors.available_vendors.price.typeError"))
        .required(t("errors.available_vendors.price.required"))
        .test(
          "range",
          t("errors.available_vendors.price.range"),
          function (value) {
            const { temp_purchase_price, temp_sale_price } = this.parent;

            if (!temp_purchase_price || !temp_sale_price) return true;

            if (value < temp_purchase_price) {
              return this.createError({
                message: `${t("errors.available_vendors.price.min")} ${temp_purchase_price}`,
              });
            }

            if (value > temp_sale_price) {
              return this.createError({
                message: `Max: ${temp_sale_price}`,
              });
            }

            return true;
          }
        ),

      currency_code: yup
        .string()
        .trim()
        .uppercase()
        .length(3, t("errors.available_vendors.currency_code.length"))
        .required(t("errors.available_vendors.currency_code.required")),
    })
  ),

});

const vat_percentages = [
  {
    label: '0',
    value: 0
  },
  {
    label: '5',
    value: 5
  },
  {
    label: '15',
    value: 15
  }
]

export function AddProduct({ isEdit = false, isView = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isShowLoader, setIsShowLoader] = useState(true);
  const [addedProductVarientColor, setAddedProductVarientColor] = useState([]);
  const { currencies } = useSelector((state) => state.currentCurrency);

  const { t } = useTranslation('product');
  const { t: ct } = useTranslation("common");

  useEffect(() => {
    dispatch(fetchMainCategories());
    dispatch(fetchVendor());
    dispatch(fetchBrand());
    dispatch(fetchColorAttribute());
    dispatch(fetchSizeAttribute());
    dispatch(fetchProductAttribute());
    dispatch(fetchCountry());
    dispatch(getCurrencies());

    if (selectedProduct && selectedProduct?.id) {
      dispatch(fetchProductVarient(selectedProduct.id));
      setValue('product_id', selectedProduct.id);
    }
    if (!isEdit) {
      setIsShowLoader(false);
    }
  }, [dispatch, isEdit]);

  const { main_categories, categories, sub_categories, vendors, product_varients, brands, product_color_attributs, product_size_attributs, product_attributs, product_attribute_values, loading } = useSelector((state) => state.product);
  const { countries } = useSelector((s) => s.internalUser);
  const { cities } = useSelector((s) => s.product);
  const selectedProduct = useSelector((s) => s.product.selected);
  const loginUser = useSelector((s) => s.user.user);

  const getDefaultVariantPrices = () => {
    return (currencies || [])
      .map((currency) => ({
        currency_id: currency.id,
        base_price: null,
        sale_price: null,
      }));
  };

  const initialForm = {
    sku_number: "",
    name: "",
    category_id: "",
    short_description: "",
    installation_charge: {},
    vat_tax: "",
    is_featured: "no",
    status: "draft",
    description: "",
    brand_id: "",
    // product_variants: [
    //   { attribute_value_color_id: "", attribute_value_size_id: "", base_price: null,sale_price :null},
    // ],
    product_variants: [
      {
        attribute_value_color_id: "",
        attribute_value_size_id: "",
        prices: getDefaultVariantPrices(),
      },
    ],
    product_attributes: [{ attribute_id: "", attribute_value_id: "" }],
    color_attributes: [],
    sub_category_id: null,
    main_category_id: null,
    available_vendors: []
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema(t)),
    defaultValues: initialForm
  });

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(addProduct(data)).unwrap();
      if (result.status) {
        Toaster.success(result.message || t("success.productAdded"));
        dispatch(resetStatus());
        reset();
        navigate('/dashboard/products');
      } else {
        Toaster.error(result.message || ct("errors.somethingWentWrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("errors.failedToAddProduct"));
      dispatch(resetStatus());
    }
  };

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(!open);

  const [colorSizeAttributeId, setColorSizeAttributeId] = useState({ color_id: null, size_id: null });

  const { fields: productVariantFields, append: appendProductVariant, remove: removeProductVariant, } = useFieldArray({
    control,
    name: "product_variants",
  });

  const { fields: productAttributeFields, append: appendProductAttribute, remove: removeProductAttribute, } = useFieldArray({
    control,
    name: "product_attributes",
  });

  const { fields: colorAttributeFields, append: appendColorAttribute, remove: removeColorAttribute, } = useFieldArray({
    control,
    name: "color_attributes",
  });

  const { fields: availableVendorFields, append: appendAvailableVendor, remove: removeAvailableVendor, replace: replaceAvailableVendor, } = useFieldArray({
    control,
    name: "available_vendors",
  });

  const clearAvailableVendorsOnVariantChange = (
    variantIndex,
    currentValue,
    nextValue
  ) => {
    if (
      !isEdit ||
      isView ||
      String(currentValue ?? "") === String(nextValue ?? "")
    ) {
      return;
    }

    const currentVariantId = getValues(
      `product_variants.${variantIndex}.id`
    );

    if (!currentVariantId) {
      return;
    }

    const vendors = getValues("available_vendors") || [];

    // Get indexes that need removal
    const indexesToRemove = vendors
      .map((vendorRow, index) =>
        String(vendorRow.product_varient_id) === String(currentVariantId)
          ? index
          : -1
      )
      .filter((index) => index !== -1)
      .sort((a, b) => b - a); // remove from end to avoid index shifting

    indexesToRemove.forEach((index) => {
      removeAvailableVendor(index);
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    files.forEach((file) => {
      appendColorAttribute({
        color_attribute_id: colorSizeAttributeId?.color_id,
        size_attribute_id: colorSizeAttributeId?.size_id,
        image: file,
      });
    });
  };

  const removeImageFromUI = (index) => {
    removeColorAttribute(index);
  };

  const removeImage = (index, imageId = null) => {
    if (imageId !== null) {
      Swal.fire({
        title: ct("areYouSure"),
        text: ct("cantRevert"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: ct("yesDeleteIt")
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(deleteProductImage(imageId)).unwrap().then((respone) => {
            Toaster.success(t("success.imageDeleted"));
            removeImageFromUI(index);
          }).catch((error) => {
            console.error("Delete failed:", error);
            Toaster.error(ct("errors.deleteFailed"));
          });
        }
      });
    } else {
      removeImageFromUI(index);
    }
  };

  useEffect(() => {
    if (isEdit) {
      if (selectedProduct && String(selectedProduct.id) === String(id)) {
        dispatch(fetchCategories(selectedProduct.main_category_id));
        dispatch(fetchSubCategories(selectedProduct.category_id));
        reset({ ...selectedProduct });
        if (selectedProduct.product_attributes?.length) {
          selectedProduct.product_attributes.forEach((row, index) => {
            dispatch(fetchProductAttributeValue({
              attribute_id: row.attribute_id,
              index: index
            }));
          });
        }
        setIsShowLoader(false);
        selectedProduct?.available_vendors?.map((row, index) => {
          dispatch(fetchCity({ country_id: row?.country_id, index: index }));
        });
      }
    }
  }, [isEdit, id, selectedProduct, dispatch]);


  const colorAttributeAdded = () => {
    dispatch(fetchColorAttribute());
  }

  const color_attributes = watch('color_attributes');
  useEffect(() => {
    if (!product_color_attributs) return;
    const filtered = product_color_attributs.filter(pc =>
      color_attributes?.some(ca => ca.color_attribute_id === pc.value)
    );
    setAddedProductVarientColor(filtered || []);

    const sizefiltered = product_size_attributs.filter(pc =>
      color_attributes?.some(ca => ca.size_attribute_id === pc.value)
    );
    if (sizefiltered) {
      setColorSizeAttributeId({ color_id: filtered[0]?.value, size_id: sizefiltered[0]?.value })
    }
  }, [
    product_color_attributs,
  ]);

  const defaultColorSelected = (color_id, size_id) => {
    if (colorSizeAttributeId?.color_id == null || colorSizeAttributeId?.size_id == null) {
      setColorSizeAttributeId({ color_id: color_id, size_id: size_id });
    }
  }

  const currencyOptions =
    currencies?.map((currency) => ({
      value: currency.currency_code,
      label: `${currency.currency_code} - ${currency.currency_name}`,
    })) || [];


  // const onColorVarientChange = (id) => {
  //   if (!id) return;

  //   const filteredColors = product_color_attributs?.filter(pc => pc.value === id);
  //   if (!filteredColors || filteredColors.length === 0) return;

  //   const selectedColor = filteredColors[0];

  //   setAddedProductVarientColor((prev) => {
  //     const exists = prev.some(item => item.value === selectedColor.value);

  //     if (exists) {
  //       return prev; 
  //     }

  //     return [...prev, selectedColor];
  //   });
  //   if(selectedColor){
  //     setColorSizeAttributeId(selectedColor?.value);
  //   }
  // };

  // const removeColorVarient = (index, color_attribute_id) => {
  //   console.log(color_attribute_id);
  //   console.log(addedProductVarientColor);
  //   setAddedProductVarientColor((prev) => {
  //     const matchingItems = addedProductVarientColor.filter(item => item.value === color_attribute_id);
  //     console.log(matchingItems);
  //     if (matchingItems.length === 1) {
  //       return prev.filter(item => item.value !== color_attribute_id);
  //     }
  //     return prev;
  //   });
  //   removeProductVariant(index); 
  // };


  const handleDefaultCurrencyPriceChange = (
    variantIndex,
    currencyIndex,
    field,
    value
  ) => {
    // Update the default currency field
    setValue(
      `product_variants.${variantIndex}.prices.${currencyIndex}.${field}`,
      value,
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );

    const defaultCurrency = currencies?.find(
      (currency) => currency.is_default
    );

    if (!defaultCurrency) return;

    const defaultCurrencyIndex = currencies.findIndex(
      (currency) => currency.id === defaultCurrency.id
    );

    // Only calculate when changing the DEFAULT currency
    if (currencyIndex !== defaultCurrencyIndex) return;

    const numericValue = parseFloat(value);

    if (isNaN(numericValue)) {
      // Clear calculated currencies if default price is cleared
      currencies.forEach((currency, index) => {
        if (currency.id === defaultCurrency.id) return;

        setValue(
          `product_variants.${variantIndex}.prices.${index}.${field}`,
          "",
          {
            shouldDirty: true,
          }
        );
      });

      return;
    }

    currencies.forEach((currency, index) => {
      if (currency.id === defaultCurrency.id) return;

      const exchangeRate = parseFloat(currency.exchange_rate);

      if (!exchangeRate || isNaN(exchangeRate)) return;

      const convertedValue = numericValue * exchangeRate;

      setValue(
        `product_variants.${variantIndex}.prices.${index}.${field}`,
        convertedValue.toFixed(currency.decimal_places ?? 2),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    });
  };

  const handleInstallationChargeChange = (changedCurrencyCode, value) => {
    const defaultCurrency = currencies?.find((c) => c.is_default);
    if (!defaultCurrency) return;

    // Always set the field that was just typed
    setValue(
      `installation_charge.${changedCurrencyCode}`,
      value,
      { shouldDirty: true, shouldValidate: true }
    );

    // Only auto-calculate when the DEFAULT currency was changed
    if (changedCurrencyCode !== defaultCurrency.currency_code) return;

    const numericValue = parseFloat(value);

    if (isNaN(numericValue) || value === "" || value === null) {
      // Clear all calculated (non-default) fields
      currencies.forEach((currency) => {
        if (currency.id === defaultCurrency.id) return;
        setValue(`installation_charge.${currency.currency_code}`, "", {
          shouldDirty: true,
        });
      });
      return;
    }

    // Auto-fill other currencies using exchange_rate
    currencies.forEach((currency) => {
      if (currency.id === defaultCurrency.id) return;
      const exchangeRate = parseFloat(currency.exchange_rate);
      if (!exchangeRate || isNaN(exchangeRate)) return;
      const convertedValue = numericValue * exchangeRate;
      setValue(
        `installation_charge.${currency.currency_code}`,
        convertedValue.toFixed(currency.decimal_places ?? 2),
        { shouldDirty: true, shouldValidate: true }
      );
    });
  };

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {(loading || isShowLoader) && <ShowLoader />}
      <ScrollToTop />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {isView ? t("viewProductTitle") : isEdit ? t("editProductTitle") : t("addNewProductTitle")}
              </Typography>
            </div>
            {!isView &&
              <div className="flex items-center gap-2">
                {!isEdit ?
                  <Button
                    onClick={handleSubmit((data) => onSubmit({ ...data, status: 'draft' }))}
                    disabled={loading}
                    type="button"
                    color="gray"
                    variant="outlined"
                    className="rounded-lg"
                  >
                    {loading ? ct("saving")
                      : !loginUser.roles.includes('Administrator')
                        ? ct("save")
                        : ct("saveToDraft")}
                  </Button> : ""}
                {loginUser.roles.includes('Administrator') && <Button type="button"
                  onClick={handleSubmit((data) => onSubmit({ ...data, status: 'active' }))}
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material"
                >
                  {loading ? ct("saving") : isEdit ? t("updateProductButton") : t("publishProductButton")}
                </Button>}
                {(!loginUser.roles.includes('Administrator') && isEdit) && <Button type="button"
                  onClick={handleSubmit((data) => onSubmit(data))}
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white"
                >
                  {loading ? ct("saving") : t("updateProductButton")}
                </Button>}
              </div>}
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/dashboard/products`} className="flex items-center text-blue-600 hover:text-blue-800" >
              <ArrowLeftIcon className="h-4 w-4 mr-1" /> {ct("back")}
            </Link>
          </div>
        </div>
        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2">
              <Typography variant="h4" color="blue-gray">
                {t("basicDetailsTitle")}
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div>
                <Typography variant="small" className="font-medium">{t("skuNumberLabel")} <span className="text-red-500">*</span>   </Typography>
                <Input
                  placeholder={t("skuNumberPlaceholder")}
                  type="text"
                  {...register("sku_number")}
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                />
                <p className="text-red-500 text-sm">{errors.sku_number?.message}</p>
              </div>
              <div>
                <Typography variant="small" className="font-medium">{t("productNameLabel")} <span className="text-red-500">*</span></Typography>
                <Input
                  type="text"
                  placeholder={t("productNamePlaceholder")}
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  {...register("name")}
                />
                <p className="text-red-500 text-sm">{errors.name?.message}</p>
              </div>
            </div>
            <div className="mt-4">
              <Typography variant="small" className="font-medium">{t("productBrandLabel")} <span className="text-red-500">*</span></Typography>
              <Select
                placeholder={t("productBrandPlaceholder")}
                onChange={(brand) => {
                  setValue('brand_id', brand.value, { shouldValidate: true });
                }}
                options={brands}
                value={brands?.find(opt => opt.value === watch('brand_id')) || null}
                isClearable
                isDisabled={isView}
              />
              <p className="text-red-500 text-sm">{errors.brand_id?.message}</p>
            </div>
            <div className="mt-4">
              <Typography variant="small" className="font-medium">{t("shortDescriptionLabel")} <span className="text-red-500">*</span></Typography>
              <Controller
                name="short_description"
                control={control}
                render={({ field }) => (
                  <ReactQuill
                    theme="snow"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("shortDescriptionPlaceholder")}
                    readOnly={isView}
                  />
                )}
              />
              <p className="text-red-500 text-sm">{errors.short_description?.message}</p>
            </div>
            <div className="mt-4">
              <Typography variant="small" className="font-medium">{t("longDescriptionLabel")} <span className="text-red-500">*</span></Typography>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <ReactQuill
                    theme="snow"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("longDescriptionPlaceholder")}
                    readOnly={isView}
                  />
                )}
              />
              <p className="text-red-500 text-sm">{errors.description?.message}</p>
            </div>
          </div>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2">
              <Typography variant="h4" color="blue-gray">{t("categoriesTitle")}</Typography>
            </div>
            <div className="w-full mb-3">
              <Typography variant="small" className="font-medium">{t("mainCategoryLabel")} <span className="text-red-500">*</span></Typography>
              <Select
                placeholder={t("mainCategoryPlaceholder")}
                onChange={(cat) => {
                  setValue('main_category_id', cat.value, { shouldValidate: true });
                  dispatch(fetchCategories(cat.value));
                  setValue('category_id', null, { shouldValidate: false });
                }}
                options={main_categories}
                value={main_categories.find(opt => opt.value === watch('main_category_id')) || null}
                isClearable
                isDisabled={isView}
              />
              <p className="text-red-500 text-sm">{errors.main_category_id?.message}</p>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div>
                <Typography variant="small" className="font-medium">{t("subCategory1Label")} <span className="text-red-500">*</span></Typography>
                <Select
                  placeholder={t("subCategory1Placeholder")}
                  onChange={(cat) => {
                    dispatch(fetchSubCategories(cat.value));
                    setValue('category_id', cat.value, { shouldValidate: true });
                    setValue('sub_category_id', null, { shouldValidate: false });
                  }}
                  options={categories}
                  value={categories.find(opt => opt.value === watch('category_id')) || null}
                  isClearable
                  isDisabled={isView || !watch('main_category_id')}
                />
                <p className="text-red-500 text-sm">{errors.category_id?.message}</p>
              </div>
              <div>
                <Typography variant="small" className="font-medium">{t("subCategory2Label")} <span className="text-red-500">*</span></Typography>
                <Select
                  placeholder={t("subCategory2Placeholder")}
                  onChange={(cat) => setValue('sub_category_id', cat?.value, { shouldValidate: true })}
                  options={sub_categories}
                  value={sub_categories.find(opt => opt.value === watch('sub_category_id')) || null}
                  isClearable
                  isDisabled={isView || !watch('category_id')}
                />
                <p className="text-red-500 text-sm">{errors.sub_category_id?.message}</p>
              </div>
            </div>
          </div>
          <div className="mt-1 mb-6 rounded-lg border border-blue-gray-100 bg-white p-5">
            {/* Header */}
            <div className="mb-6 border-b border-blue-gray-50 pb-4">
              <Typography variant="h4" color="blue-gray">
                {t("pricingTitle")}
              </Typography>
            </div>

            {/* ================= INSTALLATION CHARGE ================= */}
            <div className="mb-6">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-3 block font-medium"
              >
                {t("installationChargeLabel")}{" "}
                <span className="text-red-500">*</span>
              </Typography>

              <div className="rounded-lg border border-blue-gray-100 bg-blue-gray-50/30 p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2">
                  {currencies.map((currency) => {
                    const defaultCurrency = currencies?.find((c) => c.is_default);
                    const isDefaultCurrency = currency.id === defaultCurrency?.id;
                    return (
                      <div key={currency.id}>
                        <div className="mb-2 flex items-center justify-between">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-semibold"
                          >
                            {currency.currency_code}
                            {!isDefaultCurrency && (
                              <span className="ml-1 text-xs font-normal text-gray-400">(auto-calculated)</span>
                            )}
                          </Typography>

                          {currency.currency_symbol && (
                            <Typography variant="small" color="gray">
                              {currency.currency_symbol}
                            </Typography>
                          )}
                        </div>

                        <Controller
                          name={`installation_charge.${currency.currency_code}`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder={t("installationChargePlaceholder", {
                                currency: currency.currency_code,
                              })}
                              value={field.value ?? ""}
                              onChange={(e) => {
                                if (isDefaultCurrency) {
                                  handleInstallationChargeChange(
                                    currency.currency_code,
                                    e.target.value
                                  );
                                } else {
                                  field.onChange(e.target.value);
                                }
                              }}
                              readOnly={!isDefaultCurrency}
                              className={`!border-t-blue-gray-200 bg-white focus:!border-t-gray-900${
                                !isDefaultCurrency ? " bg-gray-50 cursor-not-allowed opacity-70" : ""
                              }`}
                              labelProps={{
                                className: "before:content-none after:content-none",
                              }}
                            />
                          )}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {errors.installation_charge?.message && (
                <p className="mt-2 text-sm text-red-500">
                  {errors.installation_charge.message}
                </p>
              )}
            </div>

            {/* ================= VAT - FULL WIDTH ================= */}
            <div className="mb-6 w-full">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 block font-medium"
              >
                {t("vatLabel")} <span className="text-red-500">*</span>
              </Typography>

              <div className="relative w-full">
                <Select
                  className="w-full"
                  classNamePrefix="vat-select"
                  placeholder={t("vatPlaceholder")}
                  onChange={(vat) =>
                    setValue("vat_tax", vat?.value, {
                      shouldValidate: true,
                    })
                  }
                  options={vat_percentages}
                  value={
                    vat_percentages.find(
                      (opt) => opt.value == watch("vat_tax")
                    ) || null
                  }
                  isClearable
                />

                <span className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 text-sm text-gray-600">
                  %
                </span>
              </div>

              {errors.vat_tax?.message && (
                <p className="mt-2 text-sm text-red-500">
                  {errors.vat_tax.message}
                </p>
              )}
            </div>

            {/* ================= FEATURED ================= */}
            <div className="border-t border-blue-gray-50 pt-4">
              <Controller
                name="is_featured"
                control={control}
                defaultValue="no"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value === "yes"}
                    onChange={(e) =>
                      field.onChange(e.target.checked ? "yes" : "no")
                    }
                    label={
                      <Typography
                        variant="small"
                        color="gray"
                        className="font-medium"
                      >
                        {t("featuredLabel")}
                      </Typography>
                    }
                    containerProps={{
                      className: "-ml-2.5",
                    }}
                  />
                )}
              />
            </div>
          </div>
          {/* Product Variant Section */}
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
            <div className="mb-5 pb-2 border-b border-blue-gray-100">
              <Typography variant="h4" color="blue-gray">
                {t("productVariantTitle")}
              </Typography>
            </div>

            <div className="space-y-6">
              {productVariantFields.map((field, index) => (
                <div
                  key={field.id}
                  className="border border-blue-gray-100 rounded-lg p-4 bg-white"
                >
                  {/* Variant Header */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">

                    {/* Color */}
                    <div className="md:col-span-5 flex flex-col w-full">
                      <Typography
                        variant="small"
                        className="font-medium mb-1 text-blue-gray-700"
                      >
                        {t("colorLabel")}{" "}
                        <span className="text-red-500">*</span>
                      </Typography>

                      <Select
                        placeholder={t("colorPlaceholder")}
                        onChange={(product_variant) => {
                          clearAvailableVendorsOnVariantChange(
                            index,
                            watch(
                              `product_variants.${index}.attribute_value_color_id`
                            ),
                            product_variant?.value
                          );

                          setValue(
                            `product_variants.${index}.attribute_value_color_id`,
                            product_variant?.value,
                            { shouldValidate: true }
                          );
                        }}
                        options={product_color_attributs}
                        value={
                          product_color_attributs?.find(
                            (opt) =>
                              opt.value ===
                              watch(
                                `product_variants.${index}.attribute_value_color_id`
                              )
                          ) || null
                        }
                        isDisabled={isView}
                        className="text-sm"
                      />

                      {errors.product_variants?.[index]
                        ?.attribute_value_color_id?.message && (
                          <p className="text-red-500 text-xs mt-1">
                            {
                              errors.product_variants[index]
                                .attribute_value_color_id.message
                            }
                          </p>
                        )}
                    </div>

                    {/* Size */}
                    <div className="md:col-span-5 flex flex-col w-full">
                      <Typography
                        variant="small"
                        className="font-medium mb-1 text-blue-gray-700"
                      >
                        {t("sizeLabel")}{" "}
                        <span className="text-red-500">*</span>
                      </Typography>

                      <Select
                        placeholder={t("sizePlaceholder")}
                        onChange={(product_variant) => {
                          clearAvailableVendorsOnVariantChange(
                            index,
                            watch(
                              `product_variants.${index}.attribute_value_size_id`
                            ),
                            product_variant?.value
                          );

                          setValue(
                            `product_variants.${index}.attribute_value_size_id`,
                            product_variant?.value,
                            { shouldValidate: true }
                          );
                        }}
                        options={product_size_attributs}
                        value={
                          product_size_attributs?.find(
                            (opt) =>
                              opt.value ===
                              watch(
                                `product_variants.${index}.attribute_value_size_id`
                              )
                          ) || null
                        }
                        isDisabled={isView}
                        className="text-sm"
                      />

                      {errors.product_variants?.[index]
                        ?.attribute_value_size_id?.message && (
                          <p className="text-red-500 text-xs mt-1">
                            {
                              errors.product_variants[index]
                                .attribute_value_size_id.message
                            }
                          </p>
                        )}
                    </div>

                    {/* Add / Remove */}
                    {!isView && (
                      <div className="md:col-span-2 flex items-center justify-end md:pt-7">
                        {index === 0 ? (
                          <button
                            type="button"
                            onClick={() =>
                              appendProductVariant({
                                attribute_value_color_id: "",
                                attribute_value_size_id: "",
                                prices: getDefaultVariantPrices(),
                              })
                            }
                            className="flex items-center justify-center rounded-md p-1 hover:bg-green-50 transition"
                          >
                            <PlusCircleIcon className="h-7 w-7 text-green-600" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => removeProductVariant(index)}
                            className="flex items-center justify-center rounded-md p-1 hover:bg-red-50 transition"
                          >
                            <MinusCircleIcon className="h-7 w-7 text-red-600" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Currency Prices */}
                  <div className="mt-5 border-t border-blue-gray-100 pt-5">
                    <div className="flex items-center justify-between mb-4">
                      <Typography
                        variant="h6"
                        color="blue-gray"
                        className="font-semibold"
                      >
                        {t("currencyPrices")}
                      </Typography>

                      <Typography
                        variant="small"
                        className="text-blue-gray-400"
                      >
                        {currencies?.length || 0}{" "}
                        {currencies?.length === 1
                          ? "Currency"
                          : "Currencies"}
                      </Typography>
                    </div>

                    <div className="space-y-3">
                      {(currencies || []).map((currency, currencyIndex) => (
                        <div
                          key={currency.id}
                          className="rounded-lg border border-blue-gray-100 bg-gray-50 p-4"
                        >
                          {/* Currency Header */}
                          <div className="flex items-center gap-2 mb-4">
                            <div className="flex items-center justify-center min-w-[45px] h-8 px-2 rounded-md bg-white border border-blue-gray-100">
                              <Typography
                                variant="small"
                                className="font-bold text-blue-gray-800"
                              >
                                {currency.currency_code}
                              </Typography>
                            </div>

                            <div>
                              <Typography
                                variant="small"
                                className="font-medium text-blue-gray-800"
                              >
                                {currency.currency_name}
                              </Typography>

                              <Typography
                                variant="small"
                                className="text-xs text-blue-gray-400"
                              >
                                {currency.currency_symbol}
                              </Typography>
                            </div>

                            {currency.is_default && (
                              <span className="ml-2 rounded-full bg-green-50 px-2 py-1 text-[10px] font-semibold text-green-600">
                                Default
                              </span>
                            )}
                          </div>

                          {/* Prices */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            {/* Purchase Price */}
                            <div className="flex flex-col">
                              <Typography
                                variant="small"
                                className="font-medium mb-1 text-blue-gray-700"
                              >
                                {t("purchasePriceLabel")}{" "}
                                {/* <span className="text-red-500">*</span> */}
                              </Typography>

                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 min-w-[28px] text-sm text-blue-gray-400">
                                  {currency.currency_symbol}
                                </span>

                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder={t("purchasePricePlaceholder")}
                                  className="w-full h-[40px] rounded-md border border-blue-gray-200 bg-white pl-12 pr-3 text-sm text-blue-gray-700 placeholder:text-blue-gray-400 focus:border-gray-900 focus:outline-none"
                                  {...register(
                                    `product_variants.${index}.prices.${currencyIndex}.base_price`,
                                    {
                                      onChange: (e) =>
                                        handleDefaultCurrencyPriceChange(
                                          index,
                                          currencyIndex,
                                          "base_price",
                                          e.target.value
                                        ),
                                    }
                                  )}
                                />
                              </div>

                              {errors.product_variants?.[index]?.prices?.[
                                currencyIndex
                              ]?.base_price?.message && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {
                                      errors.product_variants[index].prices[
                                        currencyIndex
                                      ].base_price.message
                                    }
                                  </p>
                                )}
                            </div>

                            {/* Sale Price */}
                            <div className="flex flex-col">
                              <Typography
                                variant="small"
                                className="font-medium mb-1 text-blue-gray-700"
                              >
                                {t("salePriceLabel")}{" "}
                                {/* <span className="text-red-500">*</span> */}
                              </Typography>

                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 min-w-[28px] text-sm text-blue-gray-400">
                                  {currency.currency_symbol}
                                </span>

                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder={t("salePricePlaceholder")}
                                  className="w-full h-[40px] rounded-md border border-blue-gray-200 bg-white pl-12 pr-3 text-sm text-blue-gray-700 placeholder:text-blue-gray-400 focus:border-gray-900 focus:outline-none"
                                  {...register(
                                    `product_variants.${index}.prices.${currencyIndex}.sale_price`,
                                    {
                                      onChange: (e) =>
                                        handleDefaultCurrencyPriceChange(
                                          index,
                                          currencyIndex,
                                          "sale_price",
                                          e.target.value
                                        ),
                                    }
                                  )}
                                />
                              </div>

                              {errors.product_variants?.[index]?.prices?.[
                                currencyIndex
                              ]?.sale_price?.message && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {
                                      errors.product_variants[index].prices[
                                        currencyIndex
                                      ].sale_price.message
                                    }
                                  </p>
                                )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
            <div className="mb-5 pb-2">
              <Typography variant="h4" color="blue-gray">{t("productSpecificationTitle")}</Typography>
            </div>

            {productAttributeFields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-center mb-4"
              >
                {/* Label */}
                <div className="flex flex-col">
                  <Typography variant="small" className="font-medium" >
                    {t("labelLabel")} <span className="text-red-500">*</span>
                  </Typography>
                  <Select
                    placeholder={t("labelPlaceholder")}
                    onChange={(product_attribut) => {
                      setValue(`product_attributes.${index}.attribute_id`, product_attribut.value, { shouldValidate: true });
                      setValue(`product_attributes.${index}.attribute_value_id`, "");
                      dispatch(fetchProductAttributeValue({ attribute_id: product_attribut.value, index: index }));
                    }}
                    options={product_attributs}
                    value={
                      product_attributs?.find(
                        (opt) => opt.value === watch(`product_attributes.${index}.attribute_id`)
                      ) || null
                    }
                    isClearable
                    isDisabled={isView}
                  />
                  <p className="text-red-500 text-xs mt-1 h-4 min-h-[16px]">
                    {errors.product_attributes?.[index]?.attribute_id?.message || ""}
                  </p>
                </div>

                {/* Value */}
                <div className="flex flex-col">
                  <Typography variant="small" className="font-medium" >
                    {t("valueLabel")} <span className="text-red-500">*</span>
                  </Typography>
                  <Select
                    placeholder={t("valuePlaceholder")}
                    onChange={(product_attribut) => {
                      setValue(`product_attributes.${index}.attribute_value_id`, product_attribut.value, { shouldValidate: true });
                    }}
                    options={product_attribute_values ? product_attribute_values[index] : null}
                    value={
                      (product_attribute_values?.[index] || [])?.find(
                        (opt) => opt.value === watch(`product_attributes.${index}.attribute_value_id`)
                      ) || null
                    }
                    isClearable
                    isDisabled={isView}
                  />
                  <p className="text-red-500 text-xs mt-1 h-4 min-h-[16px]">
                    {errors.product_attributes?.[index]?.attribute_value_id?.message || errors?.product_attributes?.message}
                  </p>
                </div>

                {/* Add / Remove Icons */}
                {!isView && (
                  <div className="flex justify-start items-center min-h-[80px]">
                    {index === 0 ? (
                      <PlusCircleIcon
                        className="h-6 w-6 text-green-600 cursor-pointer"
                        onClick={() => appendProductAttribute({ attribute_id: "", attribute_value_id: "" })}
                      />
                    ) : (
                      <MinusCircleIcon
                        className="h-6 w-6 text-red-600 cursor-pointer"
                        onClick={() => removeProductAttribute(index)}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {isEdit && <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2">
              <Typography variant="h4" color="blue-gray">
                {t("availableVendorsTitle")}
              </Typography>
            </div>

            <div className="flex flex-col gap-6">
              {availableVendorFields.map((field, index) => (
                <div key={field.id} className="border p-4 rounded-lg bg-gray-50 w-full">
                  {/* Product Variant */}
                  <div className="mb-4">
                    <Typography variant="small" className="font-medium" >
                      {t("productVariantLabel")} <span className="text-red-500">*</span>
                    </Typography>
                    <Select
                      placeholder={t("productVariantPlaceholder")}
                      onChange={(product_varient) =>
                        setValue(
                          `available_vendors.${index}.product_varient_id`,
                          product_varient?.value || null,
                          { shouldValidate: true }
                        )
                      }
                      options={product_varients}
                      value={
                        product_varients?.find(
                          (opt) =>
                            opt.value ===
                            watch(`available_vendors.${index}.product_varient_id`)
                        ) || null
                      }
                      isClearable
                      isDisabled={isView}
                    />
                    <p className="text-red-500 text-sm mt-1">
                      {errors.available_vendors?.[index]?.product_varient_id?.message}
                    </p>
                  </div>

                  {/* Vendors & Price/Currency */}
                  <div className="grid grid-cols-2 gap-3 items-start mb-3">
                    {/* Vendors */}
                    <div className="mb-4">
                      <Typography variant="small" className="font-medium" >
                        {t("availableVendorsLabel")} <span className="text-red-500">*</span>
                      </Typography>
                      <Select
                        placeholder={t("availableVendorsPlaceholder")}
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions
                            ? selectedOptions.map((opt) => opt.value)
                            : [];
                          setValue(
                            `available_vendors.${index}.vendor_ids`,
                            selectedValues,
                            { shouldValidate: true }
                          );
                        }}
                        options={vendors}
                        value={
                          vendors?.filter((opt) =>
                            (watch(`available_vendors.${index}.vendor_ids`) || []).includes(
                              opt.value
                            )
                          ) || []
                        }
                        isClearable
                        isMulti
                        isDisabled={isView}
                      />
                      <p className="text-red-500 text-sm mt-1">
                        {errors.available_vendors?.[index]?.vendor_ids?.message}
                      </p>
                    </div>

                    {/* Price & Currency Code Container */}
                    <div className="mb-4 grid grid-cols-2 gap-2">
                      {/* Price */}
                      <div>
                        <Typography variant="small" className="font-medium">
                          {t("priceLabel")} <span className="text-red-500">*</span>
                        </Typography>
                        <Input
                          placeholder={t("pricePlaceholder")}
                          type="number"
                          min="0"
                          {...register(`available_vendors.${index}.price`)}
                          className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                        />
                        <p className="text-red-500 text-sm mt-1">
                          {errors.available_vendors?.[index]?.price?.message}
                        </p>
                      </div>

                      {/* Currency Code */}
                      <div>
                        <Typography variant="small" className="font-medium">
                          {t("currencyCodeLabel")} <span className="text-red-500">*</span>
                        </Typography>
                        {/* <Input
                          placeholder={t("currencyCodePlaceholder")}
                          type="text"
                          maxLength={3}
                          {...register(`available_vendors.${index}.currency_code`)}
                          className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                        /> */}
                        <Select
                          placeholder={t("currencyCodePlaceholder")}
                          options={currencyOptions}
                          isDisabled={isView}
                          onChange={(selected) => {
                            setValue(`available_vendors.${index}.currency_code`, selected?.value || "", {
                              shouldValidate: true,
                            });
                          }}
                          value={
                            currencyOptions.find(
                              (option) => option.value === watch(`available_vendors.${index}.currency_code`)
                            ) || null
                          }
                          isClearable
                        />
                        <p className="text-red-500 text-sm mt-1">
                          {errors.available_vendors?.[index]?.currency_code?.message}
                        </p>
                      </div>
                    </div>

                    {/* Country */}
                    <div className="mb-4">
                      <Typography variant="small" className="font-medium" >
                        {t("countryLabel")} <span className="text-red-500">*</span>
                      </Typography>
                      <Select
                        placeholder={t("countryPlaceholder")}
                        onChange={(country) => {
                          setValue(
                            `available_vendors.${index}.country_id`,
                            country?.value || null,
                            { shouldValidate: true }
                          );
                          dispatch(fetchCity({ country_id: country?.value, index: index }));
                        }}
                        options={countries}
                        value={
                          countries?.find(
                            (opt) =>
                              opt.value ===
                              watch(`available_vendors.${index}.country_id`)
                          ) || null
                        }
                        isClearable
                        isDisabled={isView}
                      />
                      <p className="text-red-500 text-sm mt-1">
                        {errors.available_vendors?.[index]?.country_id?.message}
                      </p>
                    </div>

                    {/* City */}
                    <div className="mb-4">
                      <Typography variant="small" className="font-medium" >
                        {t("cityLabel")} <span className="text-red-500">*</span>
                      </Typography>
                      <Select
                        placeholder={t("cityPlaceholder")}
                        onChange={(city) =>
                          setValue(
                            `available_vendors.${index}.city_id`,
                            city?.value || null,
                            { shouldValidate: true }
                          )
                        }
                        options={cities?.length > 0 ? cities[index] : []}
                        value={
                          cities?.length > 0 ? cities[index]?.find(
                            (opt) =>
                              opt.value ===
                              watch(`available_vendors.${index}.city_id`)
                          ) || null : null
                        }
                        isClearable
                        isDisabled={isView}
                      />
                      <p className="text-red-500 text-sm mt-1">
                        {errors.available_vendors?.[index]?.city_id?.message}
                      </p>
                    </div>
                  </div>

                  {/* Add / Remove Buttons */}
                  <div className="flex justify-end mt-2">
                    {index === 0 ? (<>
                      <PlusCircleIcon
                        className="h-6 w-6 text-green-600 cursor-pointer"
                        onClick={() =>
                          appendAvailableVendor({
                            vendor_ids: [],
                            price: "",
                            currency_code: "",
                            product_varient_id: null,
                            country_id: null,
                            city_id: null,
                          })
                        }
                      />
                      <MinusCircleIcon
                        className="h-6 w-6 text-red-600 cursor-pointer ml-2"
                        onClick={() => removeAvailableVendor(index)}
                      /></>
                    ) : (
                      <MinusCircleIcon
                        className="h-6 w-6 text-red-600 cursor-pointer"
                        onClick={() => removeAvailableVendor(index)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

          </div>}

          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2">
              <Typography variant="h4" color="blue-gray">{t("productImageTitle")}</Typography>
            </div>
            <Typography variant="h6" className="mb-3">{t("selectColorLabel")}</Typography>
            <div className="flex gap-4">
              {/* {addedProductVarientColor?.map((item, index) => (
                <div
                  key={item.value || index}
                  onClick={() => setColorSizeAttributeId(item.value)}
                  className={`w-10 h-10 rounded-md border cursor-pointer ${colorSizeAttributeId === item.value ? "ring-4 ring-blue-500" : ""}`}
                  style={{ backgroundColor: item.color_code }}
                ></div>
              ))} */}
              <div className="flex gap-4 flex-wrap">
                {/* Watch all variants to generate the color boxes dynamically */}
                {watch("product_variants")?.map((variant, index) => {
                  const colorDetails = product_color_attributs?.find(c => c.value === variant.attribute_value_color_id);
                  const sizeDetails = product_size_attributs?.find(c => c.value === variant.attribute_value_size_id);

                  // Only show box if a color is actually selected in that row
                  if (!sizeDetails) return null;
                  if (index == 0) {
                    defaultColorSelected(colorDetails?.value, sizeDetails?.value);
                  }
                  return (
                    <div className="flex flex-col items-center gap-1" key={index}>
                      <div
                        onClick={() => setColorSizeAttributeId({ color_id: colorDetails.value, size_id: sizeDetails?.value })}
                        className={`w-10 h-10 rounded-md border cursor-pointer transition-all ${colorSizeAttributeId?.color_id === colorDetails.value && colorSizeAttributeId?.size_id === sizeDetails.value ? "ring-4 ring-blue-500 scale-110" : ""
                          }`}
                        style={{ backgroundColor: colorDetails.color_code }}
                        title={`${sizeDetails?.label}`}
                      ></div>
                      <span className="text-[10px] text-gray-500">{sizeDetails?.label}</span>
                    </div>
                  );
                })}
              </div>
              {!isView && <div>
                <PlusCircleIcon className="h-6 w-6 text-green-600 cursor-pointer mt-2" onClick={handleOpen}></PlusCircleIcon>
              </div>}
            </div>
            <div className="mt-5 mb-6">
              <Typography variant="h6" className="mb-3 pb-2">{t("uploadImageLabel")}</Typography>
              <div className="flex flex-wrap items-end gap-6">
                {!isView && <label
                  htmlFor="productImages"
                  className="cursor-pointer"
                >
                  <input
                    id="productImages"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileChange(e)}
                  />
                  <div className="h-36 w-60 bg-white border-2 border-dashed border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center shadow-sm">
                    <div className="flex flex-col items-center justify-center text-center p-4">
                      <PlusCircleIcon className="h-10 w-10 mb-2 text-blue-500" />
                      <p className="text-base font-medium">{t("uploadImageButton")}</p>
                    </div>
                  </div>
                </label>}
                {colorAttributeFields
                  .map((attribute, index) => ({ ...attribute, arrayIndex: index }))
                  .filter((attribute) => attribute.color_attribute_id === colorSizeAttributeId?.color_id && attribute.size_attribute_id === colorSizeAttributeId?.size_id)
                  .map((attribute) => (
                    // <div
                    //   key={`${attribute.color_attribute_id}-${attribute.arrayIndex}`}
                    //   className="relative w-60 h-36 border-2 border-gray-300 rounded-lg overflow-hidden shadow-md group"
                    // >
                    //   {attribute.image instanceof File ? (
                    //     attribute.image.type.startsWith("image/") ? (
                    //       <img
                    //         src={URL.createObjectURL(attribute.image)}
                    //         alt="Product Image"
                    //         className="w-full h-full object-cover"
                    //       />
                    //     ) : attribute.image.type === "application/pdf" ? (
                    //       <iframe
                    //         src={URL.createObjectURL(attribute.image)}
                    //         title="PDF Preview"
                    //         className="w-full h-full"
                    //       />
                    //     ) : (
                    //       <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-500 text-sm text-center p-2">
                    //         Unsupported file type
                    //       </div>
                    //     )
                    //   ) : (
                    //     <img
                    //       src={attribute.image}
                    //       alt="Product Image"
                    //       className="w-full h-full object-cover"
                    //     />
                    //   )}
                    //   {!isView && <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    //     <XCircleIcon
                    //       onClick={() => removeImage(attribute.arrayIndex, attribute.image instanceof File ? null : attribute._id)}
                    //       className="h-10 w-10 text-red-400 cursor-pointer hover:text-red-600 transition-colors"
                    //     />
                    //   </div>}
                    // </div>
                    <FilePreview
                      file={attribute.image}
                      onRemove={() => removeImage(attribute.arrayIndex, attribute.image instanceof File ? null : attribute._id)}
                      isView={isView}
                      containerClass="relative w-60 h-36 rounded-md overflow-hidden border border-gray-300"
                    />
                  ))}
              </div>
            </div>
            {errors.color_attributes && (
              <>
                {errors.color_attributes.message && (
                  <p className="text-red-500 text-sm">
                    {errors.color_attributes.message}
                  </p>
                )}
                {Array.isArray(errors.color_attributes) &&
                  errors.color_attributes.map((itemError, index) => {
                    if (!itemError) return null;
                    return (
                      <div key={index}>
                        {itemError.image && (
                          <p className="text-red-500 text-sm">
                            {itemError.image.message}
                          </p>
                        )}
                      </div>
                    );
                  })}
              </>
            )}

          </div>
        </fieldset>
      </form>
      <AddProductColor open={open} handler={handleOpen} colorAttributeAdded={colorAttributeAdded} />
    </div>
  );
}

export default AddProduct;
